import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  array,
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import loadable from '@loadable/component';
import classNames from 'classnames';
import omit from 'lodash/omit';
import moment from 'moment';

import { monthIdString } from '../../util/dates';
import { generateMonths } from '../../util/generators';
import { types as sdkTypes } from '../../util/sdkLoader';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import { subUnitDivisors } from '../../config/settingsCurrency';
import { useConfiguration } from '../../context/configurationContext';
import {
  displayDeliveryPickup,
  displayDeliveryShipping,
  displayPrice,
} from '../../util/configHelpers';
import {
  propTypes,
  LISTING_STATE_CLOSED,
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_ITEM,
  LINE_ITEM_HOUR,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_MULTIPLE_ITEMS,
} from '../../util/types';
import {
  convertLineItems,
  convertMoneyToDecimalJS,
  convertMoneyToNumber,
  convertPrice,
  convertUnitToSubUnit,
  decimalJSToNumber,
  formatMoney,
  unitDivisor,
} from '../../util/currency';
import { parse, stringify } from '../../util/urlHelpers';
import {
  getCustomPackagePrice,
  getRentalDateFromLocalStorage,
  sortPackages,
  userDisplayNameAsString,
} from '../../util/data';
import {
  INQUIRY_PROCESS_NAME,
  getSupportedProcessesInfo,
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

import {
  ModalInMobile,
  PrimaryButton,
  AvatarSmall,
  H1,
  H2,
  AvatarLarge,
  Ratings,
  AvatarMedium,
  NamedLink,
  Button,
} from '../../components';
import PricePackages from './PricePackages/PricePackages';

import MobileBottomNavbar from '../MobileBottomNavbar/MobileBottomNavbar';
import css from './OrderPanel.module.css';
import {
  PICKUP_AT_OWNER_LOCATION,
  REQUIRED_UPFRONT_OPTIONAL,
  USAGE_MULTIPLE,
} from '../../util/constants';
import Decimal from 'decimal.js';

const { Money } = sdkTypes;
// import { staticExchangeRates } from '../../config/configStripe';

const BookingTimeForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingTimeForm" */ './BookingTimeForm/BookingTimeForm'
  )
);
const BookingDatesForm = loadable(() =>
  import(
    /* webpackChunkName: "BookingDatesForm" */ './BookingDatesForm/BookingDatesForm'
  )
);
const InquiryWithoutPaymentForm = loadable(() =>
  import(
    /* webpackChunkName: "InquiryWithoutPaymentForm" */ './InquiryWithoutPaymentForm/InquiryWithoutPaymentForm'
  )
);
const ProductOrderForm = loadable(() =>
  import(
    /* webpackChunkName: "ProductOrderForm" */ './ProductOrderForm/ProductOrderForm'
  )
);

// This defines when ModalInMobile shows content as Modal
const MODAL_BREAKPOINT = 1023;
const TODAY = new Date();

export const PER_DAY = 'perDayPrice';
export const PER_MINUTE = 'perMinutePrice';
export const PER_HOUR = 'perHourPrice';
export const TWO_DAYS = 'twoDaysPrice';
export const WEEKLY_PRICE = 'oneWeekPrice';
export const OVERNIGHT_PRICE = 'overnightPrice';
export const TWENTY_EIGHT_DAYS = 'twentyEightDaysPrice';
const DAY_PACKAGES = [
  PER_DAY,
  TWO_DAYS,
  WEEKLY_PRICE,
  OVERNIGHT_PRICE,
  TWENTY_EIGHT_DAYS,
];

const PACKAGE_ORDER = [
  { key: PER_DAY, days: 1 },
  { key: TWO_DAYS, days: 2 },
  { key: WEEKLY_PRICE, days: 7 },
  { key: TWENTY_EIGHT_DAYS, days: 28 },
];

const isWindowDefined = typeof window !== 'undefined';
const isDesktopLayout = isWindowDefined && window.innerWidth > MODAL_BREAKPOINT;

const estimatedTotalPrice = (lineItems, marketplaceCurrency) => {
  const numericTotalPrice = lineItems.reduce((sum, lineItem) => {
    const numericPrice = convertMoneyToNumber(lineItem.lineTotal);
    return new Decimal(numericPrice).add(sum);
  }, new Decimal(0));

  // All the lineItems should have same currency so we can use the first one to check that
  // In case there are no lineItems we use currency from config.js as default
  // const currency =
  //   lineItems[0] && lineItems[0].unitPrice ? lineItems[0].unitPrice.currency : marketplaceCurrency;
   const currency =
    lineItems[0] && lineItems[0].unitPrice ? lineItems[0].unitPrice.currency : lineItems[0].unitPrice.currency;

  return new Money(
    convertUnitToSubUnit(numericTotalPrice.toNumber(), unitDivisor(currency)),
    currency
  );
};

/**
 * Get average daily price for a booking period
 * @param {*} lineItems
 * @param {*} bookingDates
 * @returns avgPrice
 */
const getLineItemsDayAvgPrice = (lineItems, bookingDates) => {
  const { bookingStart, bookingEnd } = bookingDates;

  // Calculate number of days
  const units = moment(bookingEnd).diff(moment(bookingStart), 'days');
  const itemLineItem = lineItems.find(
    i =>
      Array.isArray(i.includeFor) &&
      i.includeFor.includes('customer') &&
      i.includeFor.includes('provider')
  );

  const { lineTotal } = itemLineItem;
  const { amount, currency } = lineTotal;

  const avgAmount = amount / units;
  const avgPrice = new Money(avgAmount, currency);
  return avgPrice;
};

/**
 * Get average daily price for a package
 * @param {*} price
 * @param {*} package
 */
const getPackageAvgPrice = (price, selectedPackage, customPackage) => {
  if (price && price.amount) {

    if (customPackage && customPackage.days) {
      return new Money(price.amount / customPackage.days, price.currency);
    };

    if(customPackage && customPackage.hours){
      return new Money(price.amount / customPackage.hours, price.currency);
    };

    if(customPackage && customPackage.minutes){
      return new Money(price.amount / customPackage.minutes, price.currency);
    };

    switch (selectedPackage) {
      case PER_DAY:
        return price;
      case TWO_DAYS:
        return new Money(price.amount / 2, price.currency);
      case WEEKLY_PRICE:
        return new Money(price.amount / 7, price.currency);
      case TWENTY_EIGHT_DAYS:
        return new Money(price.amount / 28, price.currency);
      default:
        return price;
    }
  }
};

const getUnitsPrice = (price, units) => {
  const amount = convertMoneyToDecimalJS(price);
  const multipliedAmount = amount.mul(units);
  return new Money(multipliedAmount.toNumber(), price.currency);
};

const getPerUnitPrice = (price, units) => {
  const amount = convertMoneyToDecimalJS(price);
  const dividedAmount = amount.div(units);
  return new Money(dividedAmount.toNumber(), price.currency);
};

/**
 * Convert the price in current user's currency
 * @param {*} param
 * @returns converted price.
 */
const getConvertedPrice = ({
  packageAvgPrice,
  exchangeRatesReady,
  exchangeRates,
  currentUserCurrency,
}) => {
  const convertedPrice =
    !!packageAvgPrice && !!exchangeRatesReady
      ? convertPrice(
          exchangeRates,
          packageAvgPrice.amount,
          packageAvgPrice.currency,
          currentUserCurrency
        )
      : null;
  return convertedPrice;
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: `(${price.currency})`,
      priceTitle: `Unsupported currency (${price.currency})`,
    };
  }
  return {};
};

const openOrderModal = (
  isOwnListing,
  isClosed,
  history,
  location,
  selectedPackage
) => {
  if (isOwnListing || isClosed) {
    window.scrollTo(0, 0);
  } else {
    const { pathname, search, state } = location;
    const searchString = `?${stringify({
      ...parse(search),
      orderOpen: true,
      package: selectedPackage,
    })}`;
    history.push(`${pathname}${searchString}`, state);
  }
};

const closeOrderModal = (history, location) => {
  const { pathname, search, state } = location;
  const searchParams = omit(parse(search), 'orderOpen');
  const searchString = `?${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

const handleSubmit = (
  isOwnListing,
  isClosed,
  isInquiryWithoutPayment,
  onSubmit,
  history,
  location,
  selectedPackage
) => {
  // TODO: currently, inquiry-process does not have any form to ask more order data.
  // We can submit without opening any inquiry/order modal.
  return isInquiryWithoutPayment
    ? () => onSubmit({})
    : () =>
        openOrderModal(
          isOwnListing,
          isClosed,
          history,
          location,
          selectedPackage
        );
};

const dateFormattingOptions = {
  month: 'short',
  day: 'numeric',
  weekday: 'short',
};


const getSavingsHint = (range, pricePackages, intl) => {
  const { startDate, endDate } = range;
  const days = moment(endDate).diff(moment(startDate), 'days');
  const packagesMapping = {
    1: PER_DAY,
    2: TWO_DAYS,
    7: WEEKLY_PRICE,
    28: TWENTY_EIGHT_DAYS,
  };
  const availablePackages = Object.keys(pricePackages || {});
  const selectedPackage = packagesMapping[days]
  let currentPerDay = null;
  if(selectedPackage && availablePackages.includes(selectedPackage)) {
    currentPerDay = getPackageAvgPrice(pricePackages[selectedPackage], selectedPackage);
  }else{
    const customPackage = getCustomPackagePrice(days, pricePackages);
    currentPerDay = getPackageAvgPrice(customPackage, selectedPackage, { days });
  };

  let bestSavings = null;
  for (let { key, days: pkgDays } of PACKAGE_ORDER) {
    const pkg = pricePackages[key];
    if (!pkg || pkgDays <= days) continue;
    const pkgPerDay = getPackageAvgPrice(pkg, key);
    if (pkgPerDay && currentPerDay && (pkgPerDay.amount < currentPerDay.amount)) {
      const saving = currentPerDay.amount - pkgPerDay.amount;
      if (!bestSavings || saving > bestSavings.savingPerDay) {
        bestSavings = {
          betterPackageKey: key,
          betterPackageDays: pkgDays,
          savingPerDay: saving,
          additionalDays: pkgDays - days,
          formattedSaving: formatMoney(intl, new Money(saving, currentPerDay.currency)),
          newPerDayRate: pkgPerDay.amount,
        };
      }
    }
  }

  return bestSavings;
};

const handleDateRangeChange = (range, pricePackages, setSelectedPackage, setCustomPackage) => {
  const { startDate, endDate } = range;
  const days = moment(endDate).diff(moment(startDate), 'days');
  const packagesMapping = {
    1: PER_DAY,
    2: TWO_DAYS,
    7: WEEKLY_PRICE,
    28: TWENTY_EIGHT_DAYS,
  };

  const availablePackages = Object.keys(pricePackages || {});

  const selectedPackage = packagesMapping[days];
  if(selectedPackage && availablePackages.includes(selectedPackage)) {
    setSelectedPackage(selectedPackage);
    setCustomPackage(null);
    return;
  };

  // Add the custom package.
  const customPackage = getCustomPackagePrice(days, pricePackages);
  if(customPackage) {
    setCustomPackage({
      price: customPackage,
      label: `${days} days`,
      days,
    });
  }

};

const handleDateTimeRangeChange = (range, selectedPackage, pricePackages, setCustomPackage) => {
  setCustomPackage(null);
  const { bookingEndTime, bookingStartTime } = range;
  const minutes = moment(bookingEndTime).diff(moment(bookingStartTime), 'minutes');
  const hours = moment(bookingEndTime).diff(moment(bookingStartTime), 'hours');

  const calculateAmount = (price, quantity) => {
    const amount = convertMoneyToDecimalJS(price);
    const multipliedAmount = amount.mul(quantity);
    return decimalJSToNumber(multipliedAmount);
  };

  if(selectedPackage === PER_MINUTE) {
    const amount = calculateAmount(pricePackages[PER_MINUTE], minutes);
    setCustomPackage({
      price: new Money(amount, pricePackages[PER_MINUTE].currency),
      label: `${minutes} minutes`,
      minutes,
    });
  } else if (selectedPackage === PER_HOUR && hours > 1) {
    const amount = calculateAmount(pricePackages[PER_HOUR], hours);
    setCustomPackage({
      price: new Money(amount, pricePackages[PER_HOUR].currency),
      label: `${hours} hours`,
      hours,
    });
  }
};

const getCancellationMessage = (cancelation) => {
  if (cancelation === 'easy-going') {
    return 'Full refund with 72+ hours notice';
  } else if (cancelation === 'flexible') {
    return '50% refund with 72+ hours notice';
  } else if (cancelation === 'strict') {
    return 'No refund after booking is confirmed';
  }
  return cancelation;
};

const getVerificationMessage = (verification) => {
  const testVerification = (verification, value) => {
    return Array.isArray(verification) ? verification.includes(value) : verification === value;
  };

  if(testVerification(verification, 'not_required')) {
    return 'No Verification Required';
  } else if (testVerification(verification, 'minimum_of_3_successful_rent')) {
    return 'Verification – 3+ successful rentals';
  } else if (testVerification(verification, 'copy_of_drivers_licience')) {
    return 'Verification – Valid driver\'s license or passport';
  }
  return verification;
};


const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
    currentUserCurrency,
    selectedPackage,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );

  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(
    price,
    currentUserCurrency,
    intl
  );
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <H2 className={css.priceContainer}>
      <p className={css.price}>{formatMoney(intl, price)}</p>
      <div className={css.perUnit}>
        {/* <FormattedMessage id="OrderPanel.perUnit" values={{ selectedPackage }} /> */}
        {DAY_PACKAGES.includes(selectedPackage) ? (
          <FormattedMessage id="OrderPanel.perDayAvgPrice" />
        ) : (
          <FormattedMessage
            id="OrderPanel.perUnit"
            values={{ selectedPackage }}
          />
        )}
      </div>
    </H2>
  );
};


const OrderPanel = props => {
  const {
    rootClassName,
    className,
    titleClassName,
    listing,
    validListingTypes,
    lineItemUnitType: lineItemUnitTypeMaybe,
    isOwnListing,
    onSubmit,
    title,
    titleDesktop,
    author,
    authorLink,
    onManageDisableScrolling,
    onFetchTimeSlots,
    monthlyTimeSlots,
    history,
    location,
    intl,
    onFetchTransactionLineItems,
    onContactUser,
    lineItems,
    marketplaceCurrency,
    dayCountAvailableForBooking,
    marketplaceName,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    payoutDetailsWarning,
    showCartButton,
    currentUser,
    isCurrentUserListing,
    onAddToCart,
    addToCartInProgress,
    addToCartError,
    cartNotification,
    routeConfiguration,
    onlyPricingSection,
    isVerified,
    reviews,
    showAuthorDetails,
    totalListings,
    breakdownTitle,
    isShowCommissionLineItem,
    isMobile,
  } = props;

  const config = useConfiguration();
  const [bookingDates, setBookingDates] = useState(null);
  const [autoFocused, setAutoFocused] = useState(false);

  const priceKeyFromSearch = parse(location.search).package;
  const isValidSearchPriceKey = [
    PER_DAY,
    PER_HOUR,
    WEEKLY_PRICE,
    PER_MINUTE,
    TWENTY_EIGHT_DAYS,
    TWO_DAYS,
    OVERNIGHT_PRICE,
  ].includes(priceKeyFromSearch);
  const publicData = listing?.attributes?.publicData || {};
  const maxAvailableSeats = publicData?.quantity || 1;
  const access_to_rentals = publicData?.access_to_rentals;
  const cancelation_policy = publicData?.cancelation_policy;
  const Trust_and_verification = publicData?.Trust_and_verification;
  const isPickupAtOwnerLocation = access_to_rentals?.includes(
    PICKUP_AT_OWNER_LOCATION
  );
  const refundMessage = getCancellationMessage(cancelation_policy);
  const verificationMessage = getVerificationMessage(Trust_and_verification);
  
  const defaultPriceKey = publicData?.defaultPriceKey
  ? publicData.defaultPriceKey
    : Object.keys(publicData?.prices || {})[0];

  if (!defaultPriceKey) {
    return <div className={css.error}>Default price key is not not set</div>;
  }

  const { currentUserCurrency } = useSelector(state => state.user);
  const { exchangeRates, fetRatesError, fetchRatesInProgress } = useSelector(
    state => state.exchangeRate
  );

  // Keeping per day package as default one.
  const [selectedPackage, setSelectedPackage] = useState(defaultPriceKey);
  const [customPackage, setCustomPackage] = useState(null);
  const [scrolling, setScrolling] = useState(false);
  const [savingsHint, setSavingsHint] = useState(null);

  const savingPerDayMessage = savingsHint ? (
    <div className={css.savingPerDayMessage}>
      <span className={css.successMessage}><FormattedMessage id="OrderPanel.additionalDaysSaving" values={{ additionalDays: savingsHint.additionalDays }} /></span>
      <span className={css.savingPerDayMessageText}><FormattedMessage id="OrderPanel.additionalDaysSavingSaving" values={{ formattedSaving: savingsHint.formattedSaving }} /></span>
    </div>
  ) : null;

  const customerLineItems = lineItems?.filter(item => item.includeFor.includes('customer'));
  const payinTotal = customerLineItems && customerLineItems.length > 0   ? estimatedTotalPrice(customerLineItems, marketplaceCurrency) : null;
  const convertedPayinTotal = payinTotal ? convertPrice(exchangeRates, payinTotal.amount, payinTotal.currency, currentUserCurrency) : null;
  
  useEffect(() => {
    setSelectedPackage(defaultPriceKey);
  }, [defaultPriceKey]);

  const basePrice = listing.attributes.price;
  const prices = listing.attributes.publicData?.prices || {
    [defaultPriceKey]: {
      amount: basePrice.amount,
      currency: basePrice.currency,
    },
  };
  const pricePackages = exchangeRates.length
    ? sortPackages(
        Object.keys(prices).reduce((packages, key) => {
          const pack = prices[key];
          const convertedPack = convertPrice(
            exchangeRates,
            pack.amount,
            pack.currency,
            currentUserCurrency
          );
          packages[key] = convertedPack;
          return packages;
        }, {}),
        defaultPriceKey
      )
    : null;

  const { listingType, unitType, transactionProcessAlias = '' } =
    publicData || {};
  const processName = resolveLatestProcessName(
    transactionProcessAlias.split('/')[0]
  );
  const lineItemUnitType = lineItemUnitTypeMaybe || `line-item/${unitType}`;

  // const price = listing?.attributes?.price;
  const currentPrice = customPackage ?
    customPackage.price
    : pricePackages
      ? pricePackages[selectedPackage]
      : null;
  const price = !!currentPrice
    ? new Money(currentPrice.amount, currentPrice.currency)
    : null;
  const isPaymentProcess = processName !== INQUIRY_PROCESS_NAME;

  const exchangeRatesReady = !fetchRatesInProgress && exchangeRates.length;

  const lineItemsAvgPrice =
    lineItems &&
    lineItems.length &&
    bookingDates &&
    Object.keys(bookingDates) &&
    getLineItemsDayAvgPrice(lineItems, bookingDates);
  const packageAvgPrice = getPackageAvgPrice(price, selectedPackage, customPackage);

  const convertedAveragePrice = getConvertedPrice({
    packageAvgPrice,
    exchangeRatesReady,
    exchangeRates,
    currentUserCurrency,
  });

  const showPriceMissing = isPaymentProcess && !price && exchangeRates.length;
  const PriceMissing = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingPriceMissing" />
      </p>
    );
  };
  // const showInvalidCurrency = isPaymentProcess && price?.currency !== marketplaceCurrency;
  const showInvalidCurrency =
    isPaymentProcess && convertedAveragePrice?.currency !== currentUserCurrency;

  const InvalidCurrency = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.listingCurrencyInvalid" />
      </p>
    );
  };

  const showFetchRatesError =
    fetRatesError && !exchangeRates?.length && !fetchRatesInProgress;

  const FetchExchangeRatesError = () => {
    return (
      <p className={css.error}>
        <FormattedMessage id="OrderPanel.fetchExchangeRatesError" />
      </p>
    );
  };

  // We need to add dynamic dynamic fields to form for "Usage multiple calc type charges"
  const { additionalFees = [] } = publicData || {};

  // Convert the additionalFees rate to the current currency's equivalent rate
  const convertedAdditionalFees = additionalFees.map(fee => {
    const { rate } = fee;

    // Since rate is in big unit, but the money object expect in small
    // we need to convert it into small units.
    const divisor = unitDivisor(basePrice.currency);
    const updatedRate = rate * divisor;
    if (rate && exchangeRates?.length) {
      return {
        ...fee,
        rate: convertPrice(
          exchangeRates,
          updatedRate,
          basePrice.currency,
          currentUserCurrency
        ),
      };
    }
    return fee;
  });

  // Usage multiple charges require user's input regarding number of units.
  // Optional charges require user's consent to apply.
  const usageMultipleCharges =
    convertedAdditionalFees?.filter(
      fee => fee.feeCalculationType === USAGE_MULTIPLE
    ) || [];
  const otherOptionalCharges = convertedAdditionalFees?.filter(
    fee =>
      fee.feeCalculationType !== USAGE_MULTIPLE &&
      fee.whenToApply === REQUIRED_UPFRONT_OPTIONAL
  );

  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const isClosed = listing?.attributes?.state === LISTING_STATE_CLOSED;

  const isBooking = isBookingProcess(processName);
  // const shouldHaveBookingTime = isBooking && [LINE_ITEM_HOUR].includes(lineItemUnitType);
  const shouldHaveBookingTime =
    isBooking && [PER_HOUR, PER_MINUTE].includes(selectedPackage);
  const showBookingTimeForm =
    shouldHaveBookingTime && !isClosed && timeZone && exchangeRatesReady;

  // const shouldHaveBookingDates =
  //   isBooking && [LINE_ITEM_DAY, LINE_ITEM_NIGHT].includes(lineItemUnitType);
  const shouldHaveBookingDates =
    isBooking &&
    [
      PER_DAY,
      OVERNIGHT_PRICE,
      TWO_DAYS,
      WEEKLY_PRICE,
      TWENTY_EIGHT_DAYS,
    ].includes(selectedPackage);
  const showBookingDatesForm =
    shouldHaveBookingDates && !isClosed && timeZone && exchangeRatesReady;

  // The listing resource has a relationship: `currentStock`,
  // which you should include when making API calls.
  const isPurchase = isPurchaseProcess(processName);
  const currentStock = listing.currentStock?.attributes?.quantity;
  const isOutOfStock =
    isPurchase && lineItemUnitType === LINE_ITEM_ITEM && currentStock === 0;

  // Show form only when stock is fully loaded. This avoids "Out of stock" UI by
  // default before all data has been downloaded.
  const showProductOrderForm =
    isPurchase && typeof currentStock === 'number' && exchangeRatesReady;

  const showInquiryForm = processName === INQUIRY_PROCESS_NAME;

  const supportedProcessesInfo = getSupportedProcessesInfo();
  const isKnownProcess = supportedProcessesInfo
    .map(info => info.name)
    .includes(processName);

  const { pickupEnabled, shippingEnabled } =
    listing?.attributes?.publicData || {};

  const listingTypeConfig = validListingTypes.find(
    conf => conf.listingType === listingType
  );
  const displayShipping = displayDeliveryShipping(listingTypeConfig);
  const displayPickup = displayDeliveryPickup(listingTypeConfig);
  const allowOrdersOfMultipleItems = [
    STOCK_MULTIPLE_ITEMS,
    STOCK_INFINITE_MULTIPLE_ITEMS,
  ].includes(listingTypeConfig?.stockType);

  const showClosedListingHelpText = listing.id && isClosed;
  const isOrderOpen = !!parse(location.search).orderOpen;

  const subTitleText = showClosedListingHelpText
    ? intl.formatMessage({ id: 'OrderPanel.subTitleClosedListing' })
    : null;

  const authorDisplayName = userDisplayNameAsString(author, '');

  const classes = classNames(rootClassName || css.root, className);
  const titleClasses = classNames(titleClassName || css.orderTitle);

  const handlePackageSelect = selectedPackage => {
    if (!fetchLineItemsInProgress) {
      setSelectedPackage(selectedPackage);
      setCustomPackage(null);
      setAutoFocused(true);
    }
  };

  const { profile, createdAt } = author?.attributes || {};
  const { publicData: uPublicData, displayName } = profile || {};
  const { city, rating } = uPublicData || {};
  const searchString =
    !!city && !!displayName
      ? `?name=${displayName}&city=${city}`
      : !!displayName
      ? `?name=${displayName}`
      : !!city
      ? `?city=${city}`
      : '';

  const linkProps = author.id
    ? {
        name: 'ProfilePage',
        params: { id: author.id.uuid },
        to: { search: searchString },
      }
    : { name: 'ProfileBasePage' };

  const ratingPercent = '80%';
  const messageOwnerLabel = intl.formatMessage({ id: 'AboutOwner.messageOwner' });

  return (
    <>
        <div className={classes}>
          <div className={css.topBorder}>
              <div className={css.modalHeading}>
              <H1 className={css.heading}>{title}</H1>
            </div>
            <div className={css.orderHeading}>
              {titleDesktop ? (
                titleDesktop
              ) : (
                <H1 className={titleClasses}>{title}</H1>
              )}
              {subTitleText ? (
                <div className={css.orderHelp}>{subTitleText}</div>
              ) : null}
            </div>

            <div className={css.priceAndPackages} id="priceAndPackages">
              <PriceMaybe
                price={convertedAveragePrice}
                publicData={publicData}
                validListingTypes={validListingTypes}
                intl={intl}
                marketplaceCurrency={marketplaceCurrency}
                selectedCurrency={currentUserCurrency}
                selectedPackage={selectedPackage}
              />

              {!!pricePackages ? (
                <PricePackages
                  packages={pricePackages}
                  intl={intl}
                  setSelectedPackage={handlePackageSelect}
                  selectedPackage={selectedPackage}
                  currentUserCurrency={currentUserCurrency}
                  customPackage={customPackage}
                />
              ) : null}
            </div>

            {/* {bookingDates ? (
              <PromotionalTextMaybe
                bookingDates={bookingDates}
                selectedPackage={selectedPackage}
                packages={pricePackages}
                currentUserCurrency={currentUserCurrency}
                intl={intl}
              />
            ) : null} */}

            {showPriceMissing ? (
              <PriceMissing />
            ) : showFetchRatesError ? (
              <FetchExchangeRatesError />
            ) : showBookingTimeForm ? (
              <BookingTimeForm
                className={css.bookingForm}
                formId="OrderPanelBookingTimeForm"
                initialValues={{ seats: 1 }}
                lineItemUnitType={lineItemUnitType}
                onSubmit={e => onSubmit({ ...e, package: selectedPackage })}
                price={price}
                marketplaceCurrency={marketplaceCurrency}
                dayCountAvailableForBooking={dayCountAvailableForBooking}
                listingId={listing.id}
                listing={listing}
                isOwnListing={isOwnListing}
                monthlyTimeSlots={monthlyTimeSlots}
                onFetchTimeSlots={onFetchTimeSlots}
                startDatePlaceholder={intl.formatDate(
                  TODAY,
                  dateFormattingOptions
                )}
                endDatePlaceholder={intl.formatDate(
                  TODAY,
                  dateFormattingOptions
                )}
                timeZone={timeZone}
                marketplaceName={marketplaceName}
                onFetchTransactionLineItems={onFetchTransactionLineItems}
                lineItems={lineItems}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchLineItemsError={fetchLineItemsError}
                payoutDetailsWarning={payoutDetailsWarning}
                selectedPackage={selectedPackage}
                showCartButton={showCartButton}
                currentUser={currentUser}
                isCurrentUserListing={isCurrentUserListing}
                onAddToCart={onAddToCart}
                addToCartInProgress={addToCartInProgress}
                addToCartError={addToCartError}
                cartNotification={cartNotification}
                authorId={author?.id?.uuid}
                config={config}
                history={history}
                routeConfiguration={routeConfiguration}
                usageMultipleCharges={usageMultipleCharges}
                currentUserCurrency={currentUserCurrency}
                otherOptionalCharges={otherOptionalCharges}
                initialScroll={!isDesktopLayout}
                maxAvailableSeats={maxAvailableSeats}
                isPickupAtOwnerLocation={isPickupAtOwnerLocation}
                breakdownTitle={breakdownTitle}
                isShowCommissionLineItem={isShowCommissionLineItem}
                payinTotal={convertedPayinTotal}
                onTimeRangeChange={(range) => handleDateTimeRangeChange(range, selectedPackage, pricePackages, setCustomPackage)}
                removeCustomPackage={() => {
                  setCustomPackage(null);
                  setSavingsHint(null);
                }}
                refundMessage={refundMessage}
                verificationMessage={verificationMessage}
                autoFocused={autoFocused}
                customPackage={customPackage}
              />
            ) : showBookingDatesForm ? (
              <BookingDatesForm
                className={css.bookingForm}
                formId="OrderPanelBookingDatesForm"
                lineItemUnitType={lineItemUnitType}
                setBookingDates={setBookingDates}
                onSubmit={e => onSubmit({ ...e, package: selectedPackage })}
                price={price}
                initialValues={{ seats: 1 }}
                marketplaceCurrency={marketplaceCurrency}
                dayCountAvailableForBooking={dayCountAvailableForBooking}
                listingId={listing.id}
                listing={listing}
                isOwnListing={isOwnListing}
                monthlyTimeSlots={monthlyTimeSlots}
                onFetchTimeSlots={onFetchTimeSlots}
                timeZone={timeZone}
                marketplaceName={marketplaceName}
                onFetchTransactionLineItems={onFetchTransactionLineItems}
                lineItems={lineItems}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchLineItemsError={fetchLineItemsError}
                payoutDetailsWarning={payoutDetailsWarning}
                selectedPackage={selectedPackage}
                customPackage={customPackage}
                showCartButton={showCartButton}
                currentUser={currentUser}
                isCurrentUserListing={isCurrentUserListing}
                onAddToCart={onAddToCart}
                addToCartInProgress={addToCartInProgress}
                addToCartError={addToCartError}
                authorId={author?.id?.uuid}
                config={config}
                cartNotification={cartNotification}
                history={history}
                routeConfiguration={routeConfiguration}
                autoFocused={autoFocused}
                usageMultipleCharges={usageMultipleCharges}
                currentUserCurrency={currentUserCurrency}
                otherOptionalCharges={otherOptionalCharges}
                initialScroll={!isDesktopLayout}
                maxAvailableSeats={maxAvailableSeats}
                isPickupAtOwnerLocation={isPickupAtOwnerLocation}
                breakdownTitle={breakdownTitle}
                isShowCommissionLineItem={isShowCommissionLineItem}
                payinTotal={convertedPayinTotal}
                onDateRangeChange={(range) => {
                  handleDateRangeChange(range, pricePackages, setSelectedPackage, setCustomPackage);
                  const savingsHint = getSavingsHint(range, pricePackages, intl);
                  setSavingsHint(savingsHint);
                }}
                removeCustomPackage={() => {
                  setCustomPackage(null);
                  setSavingsHint(null);
                }}
                savingPerDayMessage={savingPerDayMessage}
                refundMessage={refundMessage}
                verificationMessage={verificationMessage}
              />
            ) : showProductOrderForm ? (
              <ProductOrderForm
                formId="OrderPanelProductOrderForm"
                onSubmit={onSubmit}
                price={price}
                marketplaceCurrency={marketplaceCurrency}
                currentStock={currentStock}
                allowOrdersOfMultipleItems={allowOrdersOfMultipleItems}
                pickupEnabled={pickupEnabled && displayPickup}
                shippingEnabled={shippingEnabled && displayShipping}
                displayDeliveryMethod={displayPickup || displayShipping}
                listingId={listing.id}
                isOwnListing={isOwnListing}
                marketplaceName={marketplaceName}
                onFetchTransactionLineItems={onFetchTransactionLineItems}
                onContactUser={onContactUser}
                lineItems={lineItems}
                fetchLineItemsInProgress={fetchLineItemsInProgress}
                fetchLineItemsError={fetchLineItemsError}
                payoutDetailsWarning={payoutDetailsWarning}
              />
            ) : showInquiryForm ? (
              <InquiryWithoutPaymentForm
                formId="OrderPanelInquiryForm"
                onSubmit={onSubmit}
              />
            ) : !isKnownProcess ? (
              <p className={css.errorSidebar}>
                <FormattedMessage id="OrderPanel.unknownTransactionProcess" />
              </p>
            ) : null}
          </div>
          {/* </ModalInMobile> */}

          <div className={css.openOrderFormContainer}>
            <MobileBottomNavbar setScrolling={setScrolling} />
            <div
              className={classNames(
                css.openOrderForm,
                !scrolling ? css.openOrderFormMargin : null
              )}
            >
              <PrimaryButton
                onClick={() =>
                  typeof window !== 'undefined'
                    ? (window.location.href = `tel:${process.env.REACT_APP_TEAM_PHONE_NUMBER}`)
                    : null
                }
                className={css.callButton}
              >
                <FormattedMessage id="OrderPanel.callButtonText" />
              </PrimaryButton>

              {isClosed ? (
                <div className={css.closedListingButton}>
                  <FormattedMessage id="OrderPanel.closedListingButtonText" />
                </div>
              ) : (
                <PrimaryButton
                  onClick={handleSubmit(
                    isOwnListing,
                    isClosed,
                    showInquiryForm,
                    onSubmit,
                    history,
                    location,
                    selectedPackage
                  )}
                  className={css.reserveButton}
                  disabled={isOutOfStock}
                >
                  {isBooking ? (
                    <FormattedMessage id="OrderPanel.ctaButtonMessageBooking" />
                  ) : isOutOfStock ? (
                    <FormattedMessage id="OrderPanel.ctaButtonMessageNoStock" />
                  ) : isPurchase ? (
                    <FormattedMessage id="OrderPanel.ctaButtonMessagePurchase" />
                  ) : (
                    <FormattedMessage id="OrderPanel.ctaButtonMessageInquiry" />
                  )}
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      {showAuthorDetails ? (
         <div className={classNames(css.author, css.authorDesktop)}>
         <div className={css.ownerText}>Owner</div>
         <div className={css.authorTopContent}>
           <AvatarMedium user={author} className={css.avatarMedium} />
           <div className={css.providerNameLinked}>
             <span>
               <FormattedMessage
                 id="OrderPanel.author"
                 values={{ name: authorLink }}
               />
             </span>
             <div className={css.memberSince}>
               Member Since {moment(author.createdAt).format('YYYY')}
             </div>

             <div>
               <a className={css.reviews} href="#reviews">
                 <svg width="48" height="48" viewBox="0 0 48 48">
                   <defs>
                     <linearGradient id="starGradient">
                       <stop offset={ratingPercent} stop-color="#FDB022" />
                       <stop offset={ratingPercent} stop-color="#E0E0E0" />
                       <stop offset="100%" stop-color="#E0E0E0" />
                     </linearGradient>
                   </defs>
                   <polygon
                     points="24,4 30.9,17.1 45,18.5 34,29 37,43 24,36 11,43 14,29 3,18.5 17.1,17.1"
                     fill="url(#starGradient)"
                     stroke=""
                     stroke-width="2"
                   />
                 </svg>
                 <span className={css.ratingNumber}>{(rating || 5).toFixed(1)}</span>
                 <span className={css.ratingText}>
                   <FormattedMessage
                     id="OrderPanel.reviewsCount"
                     values={{ count: reviews?.length }}
                   />
                 </span>
               </a>
               <span className={css.dividerLine}> | </span>
               <NamedLink className={css.totalListings} {...linkProps}>
                 {totalListings}{' '}
                 {totalListings > 1 ? 'Listings' : 'Listing'}
               </NamedLink>
             </div>
           </div>
         </div>
         <div className={css.authorActions}>
           <Button className={css.contactButton} type='button' variant="danger" arrow='left' onClick={() => !isOwnListing ? onContactUser(author) : null} icon={<i className="ri-message-3-line"></i>}>{messageOwnerLabel}</Button>
         </div>
       </div>
          ) : null}
    </>
  );
};

OrderPanel.defaultProps = {
  rootClassName: null,
  className: null,
  titleClassName: null,
  isOwnListing: false,
  authorLink: null,
  payoutDetailsWarning: null,
  titleDesktop: null,
  subTitle: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

OrderPanel.propTypes = {
  rootClassName: string,
  className: string,
  titleClassName: string,
  listing: oneOfType([propTypes.listing, propTypes.ownListing]),
  validListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionType: shape({
        process: string.isRequired,
        alias: string.isRequired,
        unitType: string.isRequired,
      }).isRequired,
    })
  ).isRequired,
  isOwnListing: bool,
  author: oneOfType([propTypes.user, propTypes.currentUser]).isRequired,
  authorLink: node,
  payoutDetailsWarning: node,
  onSubmit: func.isRequired,
  title: oneOfType([node, string]).isRequired,
  titleDesktop: node,
  subTitle: oneOfType([node, string]),
  onManageDisableScrolling: func.isRequired,

  onFetchTimeSlots: func.isRequired,
  monthlyTimeSlots: object,
  onFetchTransactionLineItems: func.isRequired,
  onContactUser: func,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  marketplaceCurrency: string.isRequired,
  dayCountAvailableForBooking: number.isRequired,
  marketplaceName: string.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string,
  }).isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default compose(
  withRouter,
  injectIntl
)(OrderPanel);
